import { Printer } from '../models/PrinterModel';
import * as Icons from '@shared/components/ui/icons/warehouse.icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircle, faXmark } from '@fortawesome/free-solid-svg-icons';
import React from 'react';

// 🎯 Servicio para mapear impresoras al formato del dropdown
export class PrintersDropdownService {
  // 🎯 Mapear impresoras a estructura de dropdown
  static mapPrintersToDropdown(
    printers: Printer[],
    selectedPrinterId: number | null,
    onSelect: (printerId: number | null) => void,
    labelKey: string,
    isLoading: boolean = false,
    iconColorClass: string = '!text-icons', // ✅ Clase de color para iconos
    iconColorStyle: React.CSSProperties = { color: 'var(--wrh-icons)' } // ✅ Style de color para iconos
  ): {
    label: string;
    icon: React.ReactNode;
    children: Array<{
      type?: 'label';
      label?: React.ReactNode;
      onClick?: () => void;
    }>;
  } {
    const getStatusColor = (status?: string) => {
      if (status === 'online') return 'text-green-500';
      if (status === 'offline') return 'text-red-500';
      return 'text-muted-foreground';
    };

    const selectedPrinter = printers.find((p) => p.id === selectedPrinterId);

    return {
      label: labelKey,
      icon: (
        <span
          className={`relative inline-flex items-center ${iconColorClass} ${isLoading ? 'opacity-50' : ''}`}
          style={iconColorStyle}
        >
          <Icons.ImpresoraIcon className="!w-3 !h-3" />
          <FontAwesomeIcon
            icon={faCircle}
            className={`absolute -right-1 -top-1 !w-2 !h-2 ${getStatusColor(selectedPrinter?.status)}`}
          />
        </span>
      ),
      children: [
        // 🎯 Si está cargando, mostrar estado de carga
        ...(isLoading
          ? [
              {
                type: 'label' as const,
                label: (
                  <div className="flex items-center gap-2 text-xs text-complementary-text py-2">
                    <span className={`${iconColorClass} opacity-50`} style={iconColorStyle}>
                      <Icons.ImpresoraIcon className="!w-3 !h-3" />
                    </span>
                    <span className="opacity-50">Cargando impresoras...</span>
                  </div>
                ),
              },
            ]
          : // 🎯 Si no hay impresoras activas, mostrar mensaje
            printers.length === 0
            ? [
                {
                  type: 'label' as const,
                  label: (
                    <div className="flex items-center gap-2 text-xs text-complementary-text py-2">
                      <span className={iconColorClass} style={iconColorStyle}>
                        <Icons.ImpresoraIcon className="!w-3 !h-3" />
                      </span>
                      <span>No hay impresoras habilitadas</span>
                    </div>
                  ),
                },
              ]
            : [
                // 🎯 Si hay impresora seleccionada, mostrarla
                ...(selectedPrinter
                  ? [
                      {
                        type: 'label' as const,
                        label: (
                          <div className="flex items-center justify-between text-sm border-b border-border py-2 w-full">
                            <span className="flex items-center gap-2">
                              <span className={`relative inline-flex items-center ${iconColorClass}`} style={iconColorStyle}>
                                <Icons.ImpresoraIcon className="!w-3 !h-3" />
                                <FontAwesomeIcon
                                  icon={faCircle}
                                  className={`absolute -right-1 -top-1 !w-2 !h-2 ${getStatusColor(selectedPrinter.status)}`}
                                />
                              </span>
                              <span className="font-medium text-foreground">
                                {selectedPrinter.custom_name}
                              </span>
                            </span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onSelect(null);
                              }}
                              className="ml-2 text-xs hover:text-red-500 transition-colors"
                              data-testid="deselect-printer-button"
                            >
                              <FontAwesomeIcon icon={faXmark} />
                            </button>
                          </div>
                        ),
                      },
                    ]
                  : []),

                // 🎯 Mostrar impresoras disponibles
                ...printers
                  .filter((printer) => printer.id !== selectedPrinterId)
                  .map((printer) => ({
                    label: (
                      <span className="flex items-center gap-2">
                        <span className={`relative inline-flex items-center ${iconColorClass}`} style={iconColorStyle}>
                          <Icons.ImpresoraIcon className="!w-3 !h-3" />
                          <FontAwesomeIcon
                            icon={faCircle}
                            className={`absolute -right-1 -top-1 !w-2 !h-2 ${getStatusColor(printer.status)}`}
                          />
                        </span>
                        {printer.custom_name}
                      </span>
                    ),
                    onClick: () => onSelect(printer.id),
                  })),
              ]),
      ],
    };
  }
}
